import { useTranslations } from 'next-intl'
import { Fragment, useMemo } from 'react'
import { parseReadableRating } from '@/features/reviews/utils/reviews-helpers'
import { SvgFilledRatingStar } from '@/icons/svg-filled-rating-star'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IRatingProps {
    rating: number
    /**
     * Minimal style only shows one star, extended style shows the accurate number of stars
     */
    starsStyleType: 'minimal' | 'maximal'
    ratingCountStyleType: 'only-count' | 'count-and-label'
    starClassName?: string
    ratingCount?: number
    writtenRatingCount?: number
    withReadableRating?: boolean
    colorClassName?: string
    ratingClassName?: string
    labelClassName?: string
    starsClassName?: string
    labelAndStarsContainerClassName?: string
    containerClassName?: string
    withBrackets?: boolean
}

export const Rating = ({
    rating,
    starsStyleType,
    ratingCount,
    withReadableRating = true,
    writtenRatingCount,
    ratingCountStyleType,
    colorClassName,
    labelClassName,
    starClassName,
    ratingClassName,
    starsClassName,
    labelAndStarsContainerClassName,
    containerClassName,
    withBrackets,
}: IRatingProps) => {
    const t = useTranslations()

    const readableRating = useMemo(
        // Round rating to one decimal if it has a decimal, otherwise show the integer + 0
        () => parseReadableRating(rating, ratingCount),
        [rating, ratingCount]
    )

    return (
        <div className={cn('flex items-start gap-x-2', containerClassName)}>
            <div
                className={cn(
                    'flex flex-row items-center gap-x-2',
                    labelAndStarsContainerClassName
                )}
            >
                {starsStyleType === 'maximal' && withReadableRating && Boolean(readableRating) && (
                    <span className={cn('relative font-semibold', ratingClassName, colorClassName)}>
                        {readableRating}
                    </span>
                )}

                {run(() => {
                    switch (starsStyleType) {
                        case 'minimal':
                            return (
                                <div
                                    className={cn(
                                        'flex items-center justify-center relative',
                                        starsClassName
                                    )}
                                >
                                    <SvgFilledRatingStar
                                        color={
                                            ratingCount === 0 ? COLORS.grey[600] : COLORS.neotaste
                                        }
                                        className={starClassName}
                                    />
                                </div>
                            )

                        case 'maximal':
                            return (
                                <div
                                    className={cn(
                                        'flex flex-row items-center gap-x-0 relative',
                                        starsClassName
                                    )}
                                >
                                    {Array.from({ length: 5 }).map((_, i) => {
                                        return (
                                            <SvgFilledRatingStar
                                                key={i}
                                                color={
                                                    i < rating ? COLORS.neotaste : COLORS.grey[600]
                                                }
                                                className={starClassName}
                                            />
                                        )
                                    })}
                                </div>
                            )
                    }
                })}

                {starsStyleType === 'minimal' && withReadableRating && (
                    <div className="flex flex-row items-center gap-x-4">
                        <span className={cn('font-semibold', ratingClassName, colorClassName)}>
                            {readableRating}
                        </span>
                    </div>
                )}
            </div>

            {run(() => {
                if (typeof ratingCount !== 'number') return null

                return (
                    <div
                        className={cn(
                            'flex items-center font-light whitespace-nowrap',
                            labelClassName,
                            colorClassName
                        )}
                    >
                        {run(() => {
                            const node = (
                                <div className="flex flex-row gap-x-[1px] font-normal items-center">
                                    <div className={cn(labelClassName, colorClassName)}>
                                        {ratingCount}

                                        {run(() => {
                                            if (ratingCountStyleType === 'only-count') return null

                                            const withWrittenReviewsAppendix =
                                                Boolean(writtenRatingCount)

                                            switch (withWrittenReviewsAppendix) {
                                                case false:
                                                    return ` ${t('restaurants.detail.reviews.ratingsShort')}`

                                                case true:
                                                    return ` ${t('restaurants.detail.reviews.ratingsLong', { count: writtenRatingCount ?? 0 })}`
                                            }
                                        })}
                                    </div>
                                </div>
                            )

                            switch (withBrackets) {
                                case true:
                                    return <Fragment>({node})</Fragment>
                                default:
                                    return node
                            }
                        })}
                    </div>
                )
            })}
        </div>
    )
}
